import { useEffect, useRef } from "react";
import { useAuth } from "../../providers/AuthProvider";
import useFunnelTelemetry from "../../hooks/useFunnelTelemetry";
import { getProductRoleFromClaims } from "../../services/authClaims";
import {
  createAccountAccessAttemptId,
  recordAccountAccessFailure,
  recordAccountAccessRecoveryCompleted,
} from "../../services/accountAccessHealth";

// One observer below Auth/Session providers covers both login and dashboard.
export default function AuthHealthReporter() {
  const { authError, authFailureDetails, currentUser, customClaims, loading } =
    useAuth() || {};
  const lastErrorRef = useRef(null);
  const blockedRef = useRef(null);
  const { trackFunnelAction } = useFunnelTelemetry({
    flow: "account_access",
    surface: "auth_session",
  });

  useEffect(() => {
    if (loading) return;
    const userId = currentUser?.uid || "";
    if (blockedRef.current && blockedRef.current.userId !== userId) {
      blockedRef.current = null;
      lastErrorRef.current = null;
    }
    if (authError) {
      if (lastErrorRef.current === authError) return;
      lastErrorRef.current = authError;
      const episode = blockedRef.current || {
        userId,
        accessAttemptId: createAccountAccessAttemptId("claims"),
      };
      if (recordAccountAccessFailure({
        trackFunnelAction,
        stage: "auth_session_load",
        error: {
          data: authError.data,
          code: authError.code,
          attemptCount: authFailureDetails?.attemptCount,
          transportPhase: authFailureDetails?.transportPhase,
        },
        operation: authFailureDetails?.operation,
        reasonCode: authFailureDetails?.reasonCode,
        accessAttemptId: episode.accessAttemptId,
      })) blockedRef.current = episode;
      return;
    }
    lastErrorRef.current = null;
    if (
      blockedRef.current &&
      blockedRef.current.userId &&
      blockedRef.current.userId === userId &&
      !currentUser.isAnonymous &&
      getProductRoleFromClaims(customClaims)
    ) {
      const { accessAttemptId } = blockedRef.current;
      blockedRef.current = null;
      recordAccountAccessRecoveryCompleted({
        trackFunnelAction,
        stage: "auth_session_load",
        accessAttemptId,
      });
    }
  }, [
    authError,
    authFailureDetails,
    currentUser,
    customClaims,
    loading,
    trackFunnelAction,
  ]);

  return null;
}
